import { memo } from 'react';
import NumberFlow from '@number-flow/react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { buildAssetUrl } from 'root/src/client/v2/common/helpers/assetUrl';
import { getReadableError } from 'root/src/client/v2/common/helpers/getReadableError';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
import { useCreatorGiveawayRedeem } from 'root/src/client/v2/_pages/creator/redesign/useCreatorGiveawayRedeem';
import { Img } from 'root/src/client/v2/common/components/ui/image';
const RewardCard = memo(({ reward, isRedeemed, canRedeem, onRedeem }) => {
    var _a, _b;
    const { loading, success, error, run } = useAsyncAction();
    const image = (_a = buildAssetUrl(reward.image, { w: 320, q: 80, fmt: 'webp' })) !== null && _a !== void 0 ? _a : (_b = reward.game) === null || _b === void 0 ? void 0 : _b.boxArtTemplateUrl;
    const redeem = () => {
        void run(onRedeem, {
            toast: {
                loading: 'Redeeming reward...',
                success: 'Reward redeemed.',
                error: caught => getReadableError(caught, 'Could not redeem the reward.'),
            },
        });
    };
    return (<div className="glass w-52 shrink-0 rounded-2xl border border-solid border-white/[0.08] p-4">
			<div className="relative mb-3 aspect-[3/4] w-full overflow-hidden rounded-xl bg-secondary">
				{image && <Img src={image} alt={reward.name} className="h-full w-full object-cover"/>}
				{isRedeemed && (<div className="absolute inset-0 flex items-center justify-center bg-black/60">
						<CheckCircleIcon sx={{ fontSize: 40 }} className="text-neon-green"/>
					</div>)}
			</div>
			<h4 className="truncate font-display text-sm font-semibold text-foreground">{reward.name}</h4>
			{reward.projectTitle && <p className="mt-1 line-clamp-2 text-xs text-neon-cyan">{reward.projectTitle}</p>}
			<Button type="button" variant={isRedeemed ? 'secondary' : 'brand'} size="sm" loading={loading} success={success} error={error} disabled={isRedeemed || !canRedeem} onClick={redeem} className="mt-3 w-full rounded-lg text-xs font-semibold">
				{isRedeemed ? 'Redeemed' : 'Redeem'}
			</Button>
		</div>);
});
// Redesign rewrite of the legacy `RedeemModal` (legacy MUI ModalLayout + carousel + Redux). Lists the
// winner's reward options as redesign cards; each card redeems one available voucher via
// `useCreatorGiveawayRedeem` (REDEEM_REWARD through the v2 fetcher). Zero layout shift: card images use a
// fixed aspect ratio and the redeem button keeps its size through its loading state.
export const RedesignGiveawayRedeemModal = memo(({ creatorUserId, creatorMnemonicId }) => {
    const { rewards, totalVouchers, redeemedRewardIds, isLoading, redeem } = useCreatorGiveawayRedeem(creatorUserId, creatorMnemonicId);
    const claimedCount = redeemedRewardIds.length;
    const remainingVouchers = totalVouchers - claimedCount;
    return (<RedesignModalShell className="sm:max-w-3xl">
			<div className="flex w-full flex-col items-center gap-3 text-center">
				<div className="flex h-12 w-12 items-center justify-center rounded-full border border-solid border-neon-amber/30 bg-neon-amber/10">
					<EmojiEventsIcon sx={{ fontSize: 24 }} className="text-neon-amber"/>
				</div>
				<h2 className="font-display text-xl font-bold text-foreground">Select your reward</h2>
				<span className="rounded-xl bg-neon-cyan/15 px-3 py-1 text-sm font-semibold tracking-wider text-neon-cyan">
					<NumberFlow value={claimedCount}/> / <NumberFlow value={totalVouchers}/> redeemed
				</span>
			</div>

			<div className="flex w-full gap-4 overflow-x-auto pb-2">
				{isLoading && ['a', 'b', 'c'].map(id => <Skeleton key={id} className="h-72 w-52 shrink-0 rounded-2xl"/>)}

				{!isLoading && rewards.length === 0 && (<p className="w-full py-10 text-center text-sm text-muted-foreground">No reward options available.</p>)}

				{!isLoading && rewards.map(reward => (<RewardCard key={reward.id} reward={reward} isRedeemed={redeemedRewardIds.includes(reward.id)} canRedeem={remainingVouchers > 0} onRedeem={() => redeem(reward)}/>))}
			</div>
		</RedesignModalShell>);
});
